import React, { useEffect, useMemo, useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Container,
  Box,
  Select,
  MenuItem,
  TextField,
  Button,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  TablePagination,
  Chip,
  Checkbox,
  ListItemText,
  CircularProgress,
  Alert,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  FormControlLabel,
  Radio,
  Snackbar,
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import {
  ApiError,
  ConfirmPurchaseResponse,
  Invoice,
  InvoicesResponse,
  cancelPurchase,
  confirmPurchase,
  fetchInvoices,
} from '../api/rustore';

const STATUS_OPTIONS = ['CREATED', 'EXECUTED', 'CONFIRMED', 'CANCELLED', 'REJECTED', 'EXPIRED', 'PAID'];

type StatusColor = 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';

const getStatusColor = (status?: string): StatusColor => {
  switch (status) {
    case 'PAID':
      return 'info';
    case 'CONFIRMED':
      return 'success';
    case 'CANCELLED':
      return 'error';
    case 'REJECTED':
      return 'error';
    case 'EXPIRED':
      return 'warning';
    case 'EXECUTED':
      return 'primary';
    case 'CREATED':
      return 'default';
    default:
      return 'default';
  }
};

const formatPaymentInfo = (info: unknown): string => {
  if (!info) {
    return '—';
  }

  if (typeof info === 'string') {
    return info;
  }

  try {
    return JSON.stringify(info, null, 2);
  } catch (error) {
    return String(info);
  }
};

const Invoices: React.FC = () => {
  const { token, username, logout } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [selectedApp, setSelectedApp] = useState('');
  const defaultDate = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([...STATUS_OPTIONS]);
  const [selectedDate, setSelectedDate] = useState(defaultDate);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [hint, setHint] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogAction, setDialogAction] = useState<'confirm' | 'cancel'>('confirm');
  const [targetInvoice, setTargetInvoice] = useState<Invoice | null>(null);
  const [selectedEnvironment, setSelectedEnvironment] = useState<'production' | 'sandbox'>('production');
  const [snackbarState, setSnackbarState] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>(
    {
      open: false,
      message: '',
      severity: 'success',
    }
  );
  const [cooldowns, setCooldowns] = useState<Record<string, number>>({});
  const fifteenMinutesMs = 15 * 60 * 1000;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleGoHome = () => {
    navigate('/');
  };

  const appIdsQuery = useQuery<{ data: string[] }>(
    ['appIDs', token],
    async () => {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/callbacks/rustore/app-ids`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    },
    {
      enabled: Boolean(token),
      staleTime: 5 * 60 * 1000,
    }
  );

  const invoicesQuery = useQuery<InvoicesResponse, ApiError>(
    ['invoices', selectedApp, page, rowsPerPage, selectedStatuses, selectedDate, token],
    () =>
      fetchInvoices({
        appId: selectedApp,
        statuses: selectedStatuses,
        date: selectedDate,
        page,
        pageSize: rowsPerPage,
        token: token ?? '',
      }),
    {
      enabled: Boolean(token && selectedApp && selectedStatuses.length && selectedDate),
      keepPreviousData: true,
      retry: (failureCount, error) => {
        if (error?.status && error.status < 500) {
          return false;
        }
        return failureCount < 2;
      },
      onSuccess: () => setHint(null),
    }
  );

  useEffect(() => {
    if (invoicesQuery.error) {
      const error = invoicesQuery.error;
      const baseMessage = error.message || 'Failed to load invoices.';
      const lower = baseMessage.toLowerCase();
      const additions: string[] = [];

      if (lower.includes('public-token expired')) {
        additions.push('Обновите Public-Token в Vault и повторите запрос.');
      }
      if (lower.includes('purchase not found')) {
        additions.push('Проверьте корректность идентификаторов платежа.');
      }

      const fullMessage = additions.length ? `${baseMessage} ${additions.join(' ')}` : baseMessage;
      setHint(fullMessage);
    }
  }, [invoicesQuery.error]);

  const invoices = invoicesQuery.data?.data.invoices ?? [];
  const meta = invoicesQuery.data?.data;

  const handleAppChange = (event: SelectChangeEvent<string>) => {
    setSelectedApp(event.target.value);
    setPage(0);
  };

  const handleStatusChange = (event: SelectChangeEvent<string[]>) => {
    const value = typeof event.target.value === 'string' ? event.target.value.split(',') : event.target.value;
    setSelectedStatuses(value.length ? value : [...STATUS_OPTIONS]);
    setPage(0);
  };

  const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedDate(event.target.value || defaultDate);
    setPage(0);
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleResetFilters = () => {
    setSelectedStatuses([...STATUS_OPTIONS]);
    setSelectedDate(defaultDate);
    setPage(0);
  };

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['invoices', selectedApp] });
  };

  const confirmMutation = useMutation<
    ConfirmPurchaseResponse,
    ApiError,
    { appId: string; purchaseId: string; environment: 'production' | 'sandbox' }
  >(
    ({ appId, purchaseId, environment }) =>
      confirmPurchase({ appId, purchaseId, environment, token: token ?? '' }),
    {
      onSuccess: (response, variables) => {
        setSnackbarState({
          open: true,
          severity: 'success',
          message: response.message || 'Покупка успешно подтверждена.',
        });
        setDialogOpen(false);
        setTargetInvoice(null);
        setCooldowns((prev) => ({ ...prev, [`confirm:${variables.purchaseId}`]: Date.now() }));
        queryClient.invalidateQueries({ queryKey: ['invoices'], exact: false });
      },
      onError: (error) => {
        const message = error.message || 'Не удалось подтвердить покупку.';
        setSnackbarState({ open: true, severity: 'error', message });
      },
    }
  );

  const cancelMutation = useMutation<
    ConfirmPurchaseResponse,
    ApiError,
    { appId: string; purchaseId: string; environment: 'production' | 'sandbox' }
  >(
    ({ appId, purchaseId, environment }) =>
      cancelPurchase({ appId, purchaseId, environment, token: token ?? '' }),
    {
      onSuccess: (response, variables) => {
        setSnackbarState({
          open: true,
          severity: 'success',
          message: response.message || 'Холд успешно отменён.',
        });
        setDialogOpen(false);
        setTargetInvoice(null);
        setCooldowns((prev) => ({ ...prev, [`cancel:${variables.purchaseId}`]: Date.now() }));
        queryClient.invalidateQueries({ queryKey: ['invoices'], exact: false });
      },
      onError: (error) => {
        const message = error.message || 'Не удалось отменить холд.';
        setSnackbarState({ open: true, severity: 'error', message });
      },
    }
  );

  const isProcessing = confirmMutation.isLoading || cancelMutation.isLoading;

  const inferEnvironment = (status?: string) => {
    if (!status) {
      return 'production' as const;
    }
    return status.toUpperCase().includes('SANDBOX') ? 'sandbox' : ('production' as const);
  };

  const handleOpenConfirmDialog = (invoice: Invoice) => {
    if (!selectedApp && !invoice.app_id) {
      setSnackbarState({
        open: true,
        severity: 'error',
        message: 'Выберите приложение перед выполнением действий над инвойсом.',
      });
      return;
    }

    setDialogAction('confirm');
    setTargetInvoice(invoice);
    setSelectedEnvironment(inferEnvironment(invoice.invoice_status));
    setDialogOpen(true);
  };

  const handleOpenCancelDialog = (invoice: Invoice) => {
    if (!selectedApp && !invoice.app_id) {
      setSnackbarState({
        open: true,
        severity: 'error',
        message: 'Выберите приложение перед выполнением действий над инвойсом.',
      });
      return;
    }

    setDialogAction('cancel');
    setTargetInvoice(invoice);
    setSelectedEnvironment(inferEnvironment(invoice.invoice_status));
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    if (isProcessing) {
      return;
    }
    setDialogOpen(false);
    setTargetInvoice(null);
  };

  const handleEnvironmentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedEnvironment(event.target.value as 'production' | 'sandbox');
  };

  const handleDialogSubmit = () => {
    if (!targetInvoice) {
      return;
    }

    const purchaseIdRaw = targetInvoice.purchase_id;
    if (!purchaseIdRaw) {
      setSnackbarState({
        open: true,
        severity: 'error',
        message: 'Для выбранного инвойса нет идентификатора purchase_id.',
      });
      return;
    }

    const purchaseId = String(purchaseIdRaw);
    const appId = selectedApp || (targetInvoice.app_id ? String(targetInvoice.app_id) : '');

    if (!appId) {
      setSnackbarState({
        open: true,
        severity: 'error',
        message: 'Не удалось определить идентификатор приложения.',
      });
      return;
    }

    const payload = {
      appId,
      purchaseId,
      environment: selectedEnvironment,
    };

    if (dialogAction === 'confirm') {
      confirmMutation.mutate(payload);
    } else {
      cancelMutation.mutate(payload);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarState((prev) => ({ ...prev, open: false }));
  };

  const statusSummary = useMemo(() => {
    if (!selectedStatuses.length || selectedStatuses.length === STATUS_OPTIONS.length) {
      return 'Все статусы';
    }
    return selectedStatuses.join(', ');
  }, [selectedStatuses]);

  const isInitialState = !selectedApp;

  const isLoading = invoicesQuery.isLoading || invoicesQuery.isFetching;

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            RuStore API Server — Invoices
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button color="inherit" onClick={handleGoHome}>
              Home
            </Button>
            <Typography>Пользователь: {username}</Typography>
            <Button color="inherit" onClick={handleLogout}>
              Logout
            </Button>
          </Stack>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Stack spacing={3}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              Список платежей
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Просмотр инвойсов RuStore с фильтрами по статусам и дате.
            </Typography>
          </Box>

          <Paper sx={{ p: 3 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
              <Select
                value={selectedApp}
                onChange={handleAppChange}
                displayEmpty
                sx={{ minWidth: 220 }}
                renderValue={(value) => (value ? value : 'Выберите приложение')}
              >
                <MenuItem disabled value="">
                  Выберите приложение
                </MenuItem>
                {appIdsQuery.data?.data.map((appId) => (
                  <MenuItem key={appId} value={appId}>
                    {appId}
                  </MenuItem>
                ))}
              </Select>

              <Select
                multiple
                value={selectedStatuses}
                onChange={handleStatusChange}
                displayEmpty
                sx={{ minWidth: 240 }}
                renderValue={() => statusSummary}
              >
                <MenuItem disabled value="">
                  Выберите статусы
                </MenuItem>
                {STATUS_OPTIONS.map((status) => (
                  <MenuItem key={status} value={status}>
                    <Checkbox checked={selectedStatuses.indexOf(status) > -1} />
                    <ListItemText primary={status} />
                  </MenuItem>
                ))}
              </Select>

              <TextField
                label="Дата (YYYY-MM-DD)"
                type="date"
                value={selectedDate}
                onChange={handleDateChange}
                InputLabelProps={{ shrink: true }}
              />

              <Stack direction="row" spacing={1}>
                <Button variant="outlined" onClick={handleResetFilters}>
                  Сбросить
                </Button>
                <Button variant="contained" onClick={handleRefresh} disabled={!selectedApp}>
                  Обновить
                </Button>
              </Stack>
            </Stack>
          </Paper>

          {hint && (
            <Alert severity="warning" onClose={() => setHint(null)}>
              {hint}
            </Alert>
          )}

          {invoicesQuery.data && (
            <Alert severity="info">
              Ответ: {invoicesQuery.data.message} · Request ID: {invoicesQuery.data.request_id} · Timestamp: {invoicesQuery.data.timestamp}
            </Alert>
          )}

          <Paper>
            {isInitialState ? (
              <Box sx={{ p: 5, textAlign: 'center' }}>
                <Typography variant="h6">Выберите приложение, чтобы загрузить инвойсы.</Typography>
              </Box>
            ) : isLoading ? (
              <Box sx={{ p: 5, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
              </Box>
            ) : invoicesQuery.isError ? (
              <Box sx={{ p: 5, textAlign: 'center' }}>
                <Typography color="error">Не удалось загрузить инвойсы. Попробуйте обновить страницу.</Typography>
              </Box>
            ) : invoices.length === 0 ? (
              <Box sx={{ p: 5, textAlign: 'center' }}>
                <Typography>Инвойсы не найдены для выбранных фильтров.</Typography>
              </Box>
            ) : (
              <>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Invoice ID</TableCell>
                        <TableCell>Order</TableCell>
                        <TableCell>Customer</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Payment Info</TableCell>
                        <TableCell>Created</TableCell>
                        <TableCell>Updated</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {invoices.map((invoice: Invoice, index: number) => {
                        const status = invoice.invoice_status ? invoice.invoice_status.toUpperCase() : undefined;
                        const key = String(invoice.invoice_id) || `invoice-${index}`;
                        const orderInfo = invoice.order?.visual_name || invoice.order?.order_id || '—';
                        const customerInfo = invoice.owner_code ? String(invoice.owner_code) : '—';

                        return (
                          <TableRow key={key} hover>
                            <TableCell>{invoice.invoice_id ?? '—'}</TableCell>
                            <TableCell>{orderInfo}</TableCell>
                            <TableCell>{customerInfo}</TableCell>
                            <TableCell>
                              {status ? (
                                <Chip label={status} color={getStatusColor(status)} size="small" />
                              ) : (
                                '—'
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography
                                component="pre"
                                variant="body2"
                                sx={{
                                  whiteSpace: 'pre-wrap',
                                  maxHeight: 120,
                                  overflow: 'hidden',
                                }}
                              >
                                {formatPaymentInfo(invoice.payment_info)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {invoice.invoice_date ? new Date(String(invoice.invoice_date)).toLocaleString() : '—'}
                            </TableCell>
                            <TableCell>
                              {invoice.refund_date ? new Date(String(invoice.refund_date)).toLocaleString() : '—'}
                            </TableCell>
                            <TableCell align="right">
                              {(() => {
                                const statusUpper = status ?? '';

                                if (statusUpper !== 'PAID' || !invoice.purchase_id) {
                                  return '—';
                                }

                                const normalizedPurchaseId = String(invoice.purchase_id);
                                const confirmKey = `confirm:${normalizedPurchaseId}`;
                                const cancelKey = `cancel:${normalizedPurchaseId}`;
                                const confirmTriggered = cooldowns[confirmKey];
                                const cancelTriggered = cooldowns[cancelKey];
                                const confirmOnCooldown = confirmTriggered ? Date.now() - confirmTriggered < fifteenMinutesMs : false;
                                const cancelOnCooldown = cancelTriggered ? Date.now() - cancelTriggered < fifteenMinutesMs : false;

                                const baseDisableReason = !token
                                  ? 'Требуется авторизация.'
                                  : !selectedApp && !invoice.app_id
                                    ? 'Выберите приложение.'
                                    : undefined;

                                const confirmDisableReason = baseDisableReason
                                  || (confirmOnCooldown ? 'Недавно запускали подтверждение. Повторите позже.' : undefined);

                                const cancelDisableReason = baseDisableReason
                                  || (cancelOnCooldown ? 'Недавно запускали отмену. Повторите позже.' : undefined);

                                return (
                                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      onClick={() => handleOpenConfirmDialog(invoice)}
                                      disabled={Boolean(confirmDisableReason) || isProcessing}
                                      title={confirmDisableReason}
                                    >
                                      Confirm
                                    </Button>
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      color="error"
                                      onClick={() => handleOpenCancelDialog(invoice)}
                                      disabled={Boolean(cancelDisableReason) || isProcessing}
                                      title={cancelDisableReason}
                                    >
                                      Cancel hold
                                    </Button>
                                  </Stack>
                                );
                              })()}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  rowsPerPageOptions={[10, 20, 50]}
                  component="div"
                  count={meta?.total_elements ?? 0}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </>
            )}
          </Paper>
        </Stack>
      </Container>
      <Dialog open={dialogOpen} onClose={handleCloseDialog} fullWidth maxWidth="sm">
        <DialogTitle>
          {dialogAction === 'cancel' ? 'Отмена холда' : 'Подтверждение покупки'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography>
              {dialogAction === 'cancel'
                ? `Отменить холд для purchase_id ${targetInvoice?.purchase_id ?? '—'} в приложении ${selectedApp || targetInvoice?.app_id || '—'}?`
                : `Подтвердить покупку для purchase_id ${targetInvoice?.purchase_id ?? '—'} в приложении ${selectedApp || targetInvoice?.app_id || '—'}?`}
            </Typography>
            <RadioGroup
              row
              value={selectedEnvironment}
              onChange={handleEnvironmentChange}
            >
              <FormControlLabel value="production" control={<Radio />} label="Production" />
              <FormControlLabel value="sandbox" control={<Radio />} label="Sandbox" />
            </RadioGroup>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={isProcessing}>
            Закрыть
          </Button>
          <Button
            variant="contained"
            onClick={handleDialogSubmit}
            disabled={isProcessing}
          >
            {isProcessing ? 'Выполняется…' : dialogAction === 'cancel' ? 'Отменить холд' : 'Подтвердить'}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbarState.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarState.severity} sx={{ width: '100%' }}>
          {snackbarState.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Invoices;
